import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { customvalidations } from 'src/app/shared/models/customvalidations.validator';
import { IncidentalAbsence, IncidentalAbsenceReason, Session } from '../..';
import { AbsenceService } from 'src/app/my-sedgwick/services/absence.service';
import { AccessibleSupervisors } from '../../models/AccessibleSupervisors';
import { ManagerResult, srSpeak } from 'src/app/shared';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';

@Component({
    selector: 'app-new-absences',
    template: `
    <div class="app-pt3 tw-w-full tw-flex tw-flex-wrap">
      <form
        [formGroup]="newAbsenceFormGroup"
        (ngSubmit)="(null)"
        class="tw-flex tw-flex-wrap tw-w-full"
      >
        <app-date-control
          formControlName="fromDate"
          [formGroup]="newAbsenceFormGroup"
          label="From Date"
          ngDefaultControl
          class="tw-w-full md:tw-w-[33%]"
          [minValue]="minDate"
        ></app-date-control>
        <app-date-control
          formControlName="toDate"
          [formGroup]="newAbsenceFormGroup"
          label="To Date"
          ngDefaultControl
          class="tw-w-full md:tw-w-[33%]"
          [validationControl]="'To Date'"
          [startAt]="greaterDate"
          [maxValue]="maxDate"
        ></app-date-control>
        <app-manager-selector
          formControlName="managers"
          [formGroup]="newAbsenceFormGroup"
          [label]="mgrText"
          ngDefaultControl
          [managerLookupSupervisor]="true"
          class="tw-w-full md:tw-w-[33%]"
          [managerLookupOmni]="managerLookupOmni"
          [hintLabel]="mgrText"
          (supervisorSelected)="onSupervisorSelected($event)"
          [sectionName]="'Absences'"
        >
        </app-manager-selector>
        <app-checkbox-control
          formControlName="includeIndirectReports"
          [formGroup]="newAbsenceFormGroup"
          label="Include indirect reports"
          class="tw-pt-1"
        >
          ></app-checkbox-control
        >
        <div
          *ngIf="showMessage"
          [innerHTML]="errorMessage | translation: translationRequest"
          class="error-validation"
        ></div>
        <app-action-row class="tw-w-full pr8">
          <app-button
            mat-button
            right2
            emphasis="High"
            (click)="search()"
            [loading]="searching"
            class="btn-search roundbtn"
            label="Search"
          >
          </app-button>
          <app-button right1 emphasis="Low" (click)="clearForm()" label="Clear">
          </app-button>
        </app-action-row>
      </form>
      <div class="tw-w-full">
        <app-loading-panel [state]="searching ? 'Loading' : 'Loaded'">
          <ng-template #content>
            <app-absence-grid
              [absences]="absences"
              [absencesLoading]="searching"
              [reasonData]="reasonData"
              [filterEnabled]="true"
              [noDataMessage]="noDataMsg"
              [noDataThinMessage]="noDataThinMessage"
              [translationRequest]="translationRequest"
            ></app-absence-grid>
          </ng-template>
        </app-loading-panel>
      </div>
    </div>
  `,
    styles: [
        `
      .btn-clear {
        background-color: #ffffff !important;
        color: #3f464d !important;
        border: 1px solid #3f464d !important;
        font-weight: bold !important;
      }
      .btn-clear:hover {
        background-color: #f1f4f7 !important;
      }
      .btn-search {
        color: #ffffff !important;
        background-color: #007dbc !important;
        font-weight: bold;
      }

      @media (min-width: 768px) {
        .btn-search {
          /* margin-left:18px;*/
        }
        .wd34 {
          width: 34% !important;
        }
      }

      .btn-search:hover {
        background-color: #035e81 !important;
      }
    `,
    ],
    standalone: false
})
export class NewAbsencesComponent implements OnInit {
  options: string[] = [];
  defaultFromDate: any;
  defaultToDate: any;
  reasonData: IncidentalAbsenceReason[];
  searching: boolean = true;
  mgrText: string;
  showMessage: boolean = false;
  errorMessage: string = '';
  AccessibleSupervisors: AccessibleSupervisors[] = [];
  noDataMsg: string = '<strong>No results</strong><br>';
  noDataThinMessage: string = 'There are no results that match the search';
  filterByManagerEmpUnum: string = '';
  managerLookupOmni: boolean = false;

  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 2));
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 2));
  greaterDate = new Date(new Date().setDate(new Date().getDate() + 14));
  newAbsenceFormGroup = this.fb.group({
    managers: '',
    fromDate: [],
    toDate: [],
    includeIndirectReports: [],
  });

  @Input()
  translationRequest : TranslationRequest;

  constructor(
    private fb: FormBuilder,
    private elementRef: ElementRef,
    private AbsenceService: AbsenceService,
    private session: Session
  ) {
    this.newAbsenceFormGroup.controls['toDate'].addValidators(
      customvalidations.greaterThan(
        this.newAbsenceFormGroup.controls['fromDate']
      )
    );
  }

  async ngOnInit() {
    this.minDate = new Date(this.minDate.setDate(this.minDate.getDate() - 1));
    this.mgrText = `View manager's off work staff`;
    this.AccessibleSupervisors =
      await this.AbsenceService.GetAccessibleSupervisors();
    this.managerLookupOmni =
      this.session.user.roles.omni || this.session.user.roles.overseer;
    this.reasonData = await this.AbsenceService.getAbsenceReasons();
    this.defaultFromDate = new Date(new Date().setDate(new Date().getDate()));
    this.defaultToDate = new Date(
      new Date().setDate(new Date().getDate() + 14)
    );
    this.newAbsenceFormGroup.controls['fromDate'].setValue(
      this.defaultFromDate
    );
    this.newAbsenceFormGroup.controls['toDate'].setValue(this.defaultToDate);
    this.searching = false;
    this.absences = [];
    this.search();
  }

  absences: IncidentalAbsence[] = [];

  minDateValidation() {
    const fromDateVal = new Date(
      this.newAbsenceFormGroup.get('fromDate').value
    );
    const toDateVal = new Date(this.newAbsenceFormGroup.get('toDate').value);
    let isFromInvalid: boolean = false;
    let isToInvalid: boolean = false;

    if (
      fromDateVal != undefined &&
      fromDateVal != null &&
      fromDateVal.toDateString().length > 7
    ) {
      const startDateVal = new Date(fromDateVal);
      if (startDateVal < this.minDate) {
        this.errorMessage =
          'Error: The From Date cannot be earlier than 2 years from today’s date.';
        isFromInvalid = true;
        //return false;
      }
    }
    if (
      toDateVal != undefined &&
      toDateVal != null &&
      toDateVal.toDateString().length > 7
    ) {
      const endDateVal = new Date(toDateVal);
      if (endDateVal > this.maxDate) {
        this.errorMessage =
          'The To Date cannot be greater than 2 years from today’s date.';
        isToInvalid = true;
        //return false;
      }
    }

    if (isFromInvalid && isToInvalid) {
      this.errorMessage =
        'Error: The From Date cannot be earlier than 2 years from today’s date.' +
        '<br />' +
        'Error: The To Date cannot be greater than 2 years from today’s date.';
      return false;
    } else if (isFromInvalid || isToInvalid) {
      return false;
    }

    return true;
  }
  async search() {
    const searchName = this.newAbsenceFormGroup.get('managers').value;
    if (!this.newAbsenceFormGroup.valid || !this.minDateValidation()) {
      this.showMessage = true;
      return;
    } else if (this.newAbsenceFormGroup.valid) {
      this.showMessage = false;

      if (this.filterByManagerEmpUnum != 'Empty') {
        const searchParameters: any = {
          BeginDate: this.newAbsenceFormGroup.get('fromDate').value
            ? new Date(this.newAbsenceFormGroup.get('fromDate').value)
            : null,
          EndDate: this.newAbsenceFormGroup.get('toDate').value
            ? new Date(this.newAbsenceFormGroup.get('toDate').value)
            : null,
          EmpUNum: this.filterByManagerEmpUnum
            ? this.filterByManagerEmpUnum
            : '',
          IncludeIndirects: this.newAbsenceFormGroup.get(
            'includeIndirectReports'
          ).value
            ? true
            : false,
        };
        this.searching = true;
        const listAbsenceOffWork = await this.AbsenceService.getOffWorkAbsence(
          searchParameters
        );
        this.absences =
          this.AbsenceService.mergeDateToString(listAbsenceOffWork);
      }
      this.searching = false;
    }

    if (searchName && typeof searchName === 'string') {
      (document.getElementById('searchInput') as HTMLInputElement).value =
        searchName;
    }
  }

  async onSupervisorSelected(supervisor: ManagerResult) {
    this.filterByManagerEmpUnum = supervisor ? supervisor.empUnum : '';
    srSpeak('Your own manager dashboard loaded', 'polite');
  }

  clearForm() {
    this.newAbsenceFormGroup.reset();
    this.showMessage = false;
  }
}
